import { z } from 'zod';
import { DbJsonSchema } from '../../dbJsonSchemas.js';
import { ClickhouseDataset } from '../../enums/enum.js';
import { enumToZodLiteralUnion, helperSchema } from '../../zodExtensions.js';
import { variantExchange } from '../customer/schema/variantExchange.js';
import { claimCreate } from '../schema/claim.js';
import { claimNotifyByEmail } from '../schema/claimFinalize.js';
import { storeUiView, storeUiViewCreate } from '../schema/filters.js';
import { crewOrder } from '../schema/order.js';
import { apiClientResp } from './error.js';
import { CrewMerchantUi } from './schema.js';
import { ConfigureShippingMethod } from './schema/configure.js';
//* user
const userPath = `/user/:idFromPlatform`;
const userParams = z.object({ idFromPlatform: z.string() });
const userAccessCodePath = `/users`;
const userAccessCodeQuery = z.object({ accessCode: z.string() });
const signedUrlPath = `/signed-url`;
const storeIdPath = `/:storeId`;
const storeIdParams = z.object({ storeId: z.coerce.number() });
const storeIdSearchPath = `${storeIdPath}/search`;
const storeIdGenReportPath = `${storeIdPath}/report`;
const storeIdTagPath = `${storeIdPath}/tag/:tagId`;
const storeIdViewPath = `${storeIdPath}/view`;
const storeIdViewIdPath = `${storeIdViewPath}/:viewId`;
const storeIdViewIdParams = z
    .object({ viewId: z.coerce.number() })
    .merge(storeIdParams);
const storeIdTagParams = z
    .object({ tagId: z.coerce.number() })
    .merge(storeIdParams);
const storeIdShippingProtectionPath = `${storeIdPath}/shipping-protection`;
const shippingProtectionConfigurePath = `${storeIdShippingProtectionPath}/configure`;
const shippingProtectionRatePath = `${storeIdShippingProtectionPath}/rate`;
const shippingProtectionRateIdPath = `${shippingProtectionRatePath}/:rateId`;
const shippingProtectionRateIdParams = storeIdParams.merge(z.object({ rateId: z.coerce.number() }));
const shippingClaimPath = `${storeIdShippingProtectionPath}/claim`;
const registrationPath = `${storeIdPath}/registration`;
const registrationIdPath = `${registrationPath}/:registrationId`;
const storeIdProductIdFromPlatformParams = z
    .object({ idFromPlatform: z.coerce.number() })
    .merge(storeIdParams);
const storeCustomFieldPath = `${storeIdPath}/custom-field`;
const storeCustomFieldIdPath = `${storeCustomFieldPath}/:customFieldId`;
const storeRulePath = `${storeIdPath}/rule`;
const storeRuleIdPath = `${storeRulePath}/:ruleId`;
const factValuesPath = `${storeIdPath}/fact/values`;
const invoicePath = `${storeIdPath}/invoice`;
const customerPaymentPath = `${storeIdPath}/customer-payment`;
const customerPaymentIdPath = `${customerPaymentPath}/:customerPaymentId`;
const customerPaymentIdRefundPath = `${customerPaymentIdPath}/refund`;
const storeIdDashboardPath = `${storeIdPath}/dashboard`;
const storeIdDashboardLoadPath = `${storeIdDashboardPath}/load`;
const storeIdDashboardPromptPath = `${storeIdDashboardPath}/prompt`;
const settingsPath = `${storeIdPath}/settings`;
const settingsThemePath = `${settingsPath}/theme`;
const settingsConfigPath = `${settingsPath}/config`;
const settingsIntegrationPath = `${settingsPath}/integrations`;
const orderPath = `${storeIdPath}/order`;
// * return + warranty claims
const claimPath = `${storeIdPath}/claim`;
const storeClaimTagPath = `${claimPath}/tags`;
const storeClaimTagIdPath = `${storeClaimTagPath}/:tagId`;
const customerPath = `${storeIdPath}/customer`;
const customerIdPath = `${customerPath}/:customerId`;
const claimIdPath = `${claimPath}/:claimId`;
const claimIdFeesPath = `${claimIdPath}/fees`;
const claimIdCustomerInfoPath = `${claimIdPath}/customer-info`;
const claimIdTagPath = `${claimIdPath}/tags`;
const claimIdTimelinePath = `${claimIdPath}/timeline`;
const claimIdFinalizePath = `${claimIdPath}/finalize`;
const claimIdCancelPath = `${claimIdPath}/cancel`;
const claimIdShipmentPath = `${claimIdPath}/shipment`;
const claimIdShipmentQuotePath = `${claimIdShipmentPath}/quote`;
const claimIdQuoteAndCreatePath = `${claimIdShipmentPath}/quote-and-create`;
const claimIdNeedsMoreInfoPath = `${claimIdPath}/request-info`;
const claimLineItemPath = `${claimIdPath}/line-item`;
const claimLineItemIdPath = `${claimLineItemPath}/:claimLineItemId`;
const claimLineItemIdInspectionPath = `${claimLineItemIdPath}/inspection`;
const claimLineItemIdInspectionIdPath = `${claimLineItemIdInspectionPath}/:claimInspectionId`;
const claimLineItemIdInspectionSettingsPath = `${claimLineItemIdInspectionPath}/settings`;
// * shipping claim
const shippingClaimIdPath = `${shippingClaimPath}/:shippingClaimId`;
const shippingClaimIdFinalizePath = `${shippingClaimIdPath}/finalize`;
// * shipment policy
const returnLocationPath = `${storeIdPath}/return-location`;
const returnLocationIdPath = `${returnLocationPath}/:returnLocationId`;
// * claim reasons (return +)
const claimReasonPath = `${storeIdPath}/claim-reason`;
const claimReasonIdPath = `${claimReasonPath}/:claimReasonId`;
const claimReasonGroupPath = `${claimReasonPath}/group`;
const claimReasonGroupIdPath = `${claimReasonGroupPath}/:claimReasonGroupId`;
// * store products
const storeProductPath = `${storeIdPath}/product`;
const storeProductIdPath = `${storeIdPath}/product/:idFromPlatform`;
const storeProductTagPath = `${storeProductPath}/tag`;
const storeProductTypePath = `${storeProductPath}/type`;
const storeProductCollectionPath = `${storeProductPath}/collection`;
const storeProductGroupPath = `${storeProductPath}/group`;
const storeProductSyncPath = `${storeProductPath}/sync`;
const storeProductGroupIdPath = `${storeProductGroupPath}/:groupId`;
const storeProductGroupIdListProductsPath = `${storeProductGroupIdPath}/list-products`;
const storeProductGroupIdRefreshProductsPath = `${storeProductGroupIdPath}/refresh-products`;
// * store users
const storeUserPath = `${storeIdPath}/user`;
const storeUserSignupPath = `${storeUserPath}/sign-up`;
const storeUserIdPath = `${storeUserPath}/:userId`;
const storeUserPasswordResetPath = `${storeUserIdPath}/reset-password`;
// ------------------  Schemas  ------------------
const signedUrlQuery = z.object({
    filename: z.string(),
    contentType: z.string(),
});
const storeProductQuery = z.object({
    searchTerm: z.string(),
});
const storeRuleIdParams = z
    .object({ ruleId: z.coerce.number() })
    .merge(storeIdParams);
const customFieldIdParams = z
    .object({
    customFieldId: z.coerce.number(),
})
    .merge(storeIdParams);
const customerRefundIdParams = z
    .object({
    customerPaymentId: z.coerce.number(),
})
    .merge(storeIdParams);
const claimIdParams = z
    .object({ claimId: z.coerce.number() })
    .merge(storeIdParams);
const shippingClaimIdParams = z
    .object({ shippingClaimId: z.coerce.number() })
    .merge(storeIdParams);
const registrationIdParams = z
    .object({ registrationId: z.coerce.number() })
    .merge(storeIdParams);
const storeCustomerIdParams = z
    .object({
    customerId: z.coerce.number(),
})
    .merge(storeIdParams);
const storeDashboardLoadQuery = z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    dashboardId: z.string(),
});
const storeDashboardPromptQuery = z.object({
    question: z.string(),
    dataset: enumToZodLiteralUnion(ClickhouseDataset),
});
const claimLineItemIdParams = claimIdParams.merge(z.object({ claimLineItemId: z.coerce.number() }));
const claimLineItemIdInspectionParams = claimLineItemIdParams.merge(z.object({ claimInspectionId: z.coerce.number() }));
const claimReasonIdParams = z
    .object({ claimReasonId: z.coerce.number() })
    .merge(storeIdParams);
const claimReasonGroupIdParams = z
    .object({ claimReasonGroupId: z.coerce.number() })
    .merge(storeIdParams);
const returnLocationIdParams = z
    .object({ returnLocationId: z.string() })
    .merge(storeIdParams);
const storeUserIdParams = z
    .object({
    userId: z.coerce.number(),
})
    .merge(storeIdParams);
const storeProductGroupIdParams = z
    .object({
    groupId: z.coerce.number(),
})
    .merge(storeIdParams);
const storeOrderQuery = z.union([
    z.object({
        idFromPlatform: z.string(),
    }),
    z.object({
        emailOrPostalCode: z.string(),
        orderNo: z.string(),
    }),
]);
export const crewMerchantRegistrationApi = {
    [registrationIdPath]: {
        path: registrationIdPath,
        get: {
            request: { params: registrationIdParams },
            response: { body: CrewMerchantUi.registrationSchema },
        },
        put: {
            request: {
                params: registrationIdParams,
                body: CrewMerchantUi.registrationUpdateSchema,
            },
            response: { body: CrewMerchantUi.registrationSchema },
        },
        delete: {
            request: { params: registrationIdParams },
            response: { body: apiClientResp },
        },
    },
};
export const crewMerchantStoreCustomerApi = {
    [customerIdPath]: {
        path: customerIdPath,
        get: {
            request: { params: storeCustomerIdParams },
            response: { body: CrewMerchantUi.storeCustomerSchema },
        },
    },
};
export const crewMerchantShippingClaimApi = {
    [shippingClaimPath]: {
        path: shippingClaimPath,
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.shippingClaimCreateSchema,
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimIdPath]: {
        path: shippingClaimIdPath,
        get: {
            request: { params: shippingClaimIdParams },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimIdFinalizePath]: {
        path: shippingClaimIdFinalizePath,
        post: {
            request: {
                params: shippingClaimIdParams,
                body: CrewMerchantUi.shippingClaimFinalizeSchema,
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
};
/** Base CRUD Operations for a Claim */
const crewMerchantClaimCrudApi = {
    [claimPath]: {
        path: claimPath,
        post: {
            request: { params: storeIdParams, body: claimCreate },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimIdPath]: {
        path: claimIdPath,
        get: {
            request: { params: claimIdParams },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimIdFinalizePath]: {
        path: claimIdFinalizePath,
        post: {
            request: {
                params: claimIdParams,
                body: z.object({
                    notifyCustomerByEmail: claimNotifyByEmail.optional(),
                    noteToCustomer: helperSchema.optionalizedString,
                }),
            },
        },
    },
    [claimIdCancelPath]: {
        path: claimIdCancelPath,
        post: {
            request: {
                params: claimIdParams,
            },
            response: { body: apiClientResp },
        },
    },
};
/**
 * Additional Claim APIs for Updating Related Entities or Complex Property Operations
 */
const crewMerchantClaimRelationsApi = {
    [claimIdCustomerInfoPath]: {
        path: claimIdCustomerInfoPath,
        put: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimUpdateCustomerInfoSchema,
            },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimIdFeesPath]: {
        path: claimIdFeesPath,
        put: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimUpdateRefundHandlingFeeSchema,
            },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimIdTagPath]: {
        path: claimIdTagPath,
        get: {
            request: { params: claimIdParams },
            response: { body: z.array(CrewMerchantUi.claimTagSchema) },
        },
        put: {
            request: {
                params: claimIdParams,
                body: z.array(CrewMerchantUi.claimTagCreateSchema),
            },
            response: {
                body: z.array(CrewMerchantUi.claimTagSchema),
            },
        },
    },
    [claimIdNeedsMoreInfoPath]: {
        path: claimIdNeedsMoreInfoPath,
        post: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimNeedsMoreInfoSchema,
            },
        },
    },
    [claimIdShipmentPath]: {
        path: claimIdShipmentPath,
        post: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimReturnShipmentCreateSchema,
            },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimIdQuoteAndCreatePath]: {
        path: claimIdQuoteAndCreatePath,
        post: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimShipmentQuoteAndBuySchema,
            },
            response: { body: apiClientResp },
        },
    },
    [claimIdShipmentQuotePath]: {
        path: claimIdShipmentQuotePath,
        post: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimReturnShipmentQuoteSchema,
            },
            response: { body: CrewMerchantUi.claimReturnShipmentQuoteResponse },
        },
    },
    [claimLineItemIdPath]: {
        path: claimLineItemIdPath,
        put: {
            request: {
                params: claimLineItemIdParams,
                body: CrewMerchantUi.claimLineItemUpdateSchema,
            },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimLineItemIdInspectionPath]: {
        path: claimLineItemIdInspectionPath,
        post: {
            request: {
                params: claimLineItemIdParams,
                body: CrewMerchantUi.claimLineItemInspectionCreateSchema,
            },
            response: { body: CrewMerchantUi.claimLineItemInspectionSchema },
        },
    },
    [claimLineItemIdInspectionIdPath]: {
        path: claimLineItemIdInspectionIdPath,
        delete: {
            request: {
                params: claimLineItemIdInspectionParams,
            },
            response: {
                body: apiClientResp,
            },
        },
    },
    [claimIdTimelinePath]: {
        path: claimIdTimelinePath,
        post: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimTimelineEventSchema,
            },
            response: { body: apiClientResp },
        },
    },
    [claimReasonPath]: {
        path: claimReasonPath,
        get: {
            request: {
                params: storeIdParams,
            },
            response: { body: z.array(CrewMerchantUi.claimReasonSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.claimReasonCreateSchema,
            },
            response: { body: CrewMerchantUi.claimReasonSchema },
        },
    },
    [claimReasonIdPath]: {
        path: claimReasonIdPath,
        put: {
            request: {
                params: claimReasonIdParams,
                body: CrewMerchantUi.claimReasonUpdateSchema,
            },
            response: { body: CrewMerchantUi.claimReasonSchema },
        },
    },
    [claimReasonGroupPath]: {
        path: claimReasonGroupPath,
        get: {
            request: {
                params: storeIdPath,
            },
            response: { body: z.array(CrewMerchantUi.claimReasonGroupSchema) },
        },
        post: {
            request: {
                params: storeIdPath,
                body: CrewMerchantUi.claimReasonGroupCreateSchema,
            },
            response: { body: CrewMerchantUi.claimReasonGroupSchema },
        },
    },
    [claimReasonGroupIdPath]: {
        path: claimReasonGroupIdPath,
        put: {
            request: {
                params: claimReasonGroupIdParams,
                body: CrewMerchantUi.claimReasonGroupUpdateSchema,
            },
            response: { body: CrewMerchantUi.claimReasonGroupSchema },
        },
        delete: {
            request: {
                params: claimReasonGroupIdParams,
            },
            response: { body: CrewMerchantUi.apiClientRespSchema },
        },
    },
    [claimLineItemIdInspectionSettingsPath]: {
        path: claimLineItemIdInspectionSettingsPath,
        get: {
            request: {
                params: claimLineItemIdParams,
            },
            response: {
                body: CrewMerchantUi.claimInspectionSettingsSchema,
            },
        },
    },
};
/**
 * Merge of claim APIs.
 *
 * Defined with an explicit type annotation.
 * Otherwise, TypeScript will error with TS7056: 'The inferred type of this node exceeds the maximum length the compiler will serialize. An explicit type annotation is needed.'
 *
 * This so a workaround to handle the limit, but we might need to consider a different approach for our APIs leveraging the builder pattern at some point, so the types can be incrementally built/joined together.
 * A few related discussions:
 * - [Zod #1040](https://github.com/colinhacks/zod/issues/1040)
 * - [Comment from Colin McDonnell on Zod #3719](https://github.com/colinhacks/zod/issues/3719#issuecomment-2322247736) noting that Zod 4 will have simplified internals and should help with this a little.
 * - [Suggested Builder Pattern](https://github.com/microsoft/TypeScript/issues/43817#issuecomment-827746462) from [Relevant StackOverflow Answer](https://stackoverflow.com/a/69908599)
 */
export const crewMerchantClaimApi = {
    ...crewMerchantClaimCrudApi,
    ...crewMerchantClaimRelationsApi,
};
export const crewMerchantSearchApi = {
    [storeIdSearchPath]: {
        path: storeIdSearchPath,
        post: {
            request: {
                body: CrewMerchantUi.searchReqSchema,
            },
            response: {
                body: CrewMerchantUi.searchRespSchema,
            },
        },
    },
    [storeIdGenReportPath]: {
        path: storeIdGenReportPath,
        post: {
            request: {
                body: CrewMerchantUi.reportReqSchema,
            },
            response: {
                body: apiClientResp,
            },
        },
    },
    [storeIdViewPath]: {
        path: storeIdViewPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(storeUiView) },
        },
        post: {
            request: { params: storeIdParams, body: storeUiViewCreate },
        },
    },
    [storeIdViewIdPath]: {
        path: storeIdViewIdPath,
        get: {
            request: { params: storeIdViewIdParams },
            response: { body: storeUiView },
        },
        delete: {
            request: { params: storeIdViewIdParams },
            response: { body: apiClientResp },
        },
        put: {
            request: { params: storeIdViewIdParams, body: storeUiView },
            response: { body: storeUiView },
        },
    },
    [storeIdTagPath]: {
        path: storeIdTagPath,
        delete: {
            request: { params: storeIdTagParams },
            response: { body: apiClientResp },
        },
    },
    [storeIdDashboardPath]: {
        path: storeIdDashboardPath,
        get: {
            request: { params: storeIdParams },
            response: { body: DbJsonSchema.StoreSettings.dashboards },
        },
    },
    [storeIdDashboardLoadPath]: {
        path: storeIdDashboardLoadPath,
        get: {
            request: { params: storeIdParams, query: storeDashboardLoadQuery },
            response: { body: CrewMerchantUi.supersetTokenSchema },
        },
    },
    [storeIdDashboardPromptPath]: {
        path: storeIdDashboardPromptPath,
        get: {
            request: {
                params: storeIdParams,
                query: storeDashboardPromptQuery,
            },
            response: {
                body: z.object({
                    answer: z.string(),
                }),
            },
        },
    },
    [userAccessCodePath]: {
        path: userAccessCodePath,
        get: {
            request: { query: userAccessCodeQuery },
            response: { body: CrewMerchantUi.userAccessCodeSchema },
        },
    },
    [signedUrlPath]: {
        path: signedUrlPath,
        get: {
            request: { query: signedUrlQuery },
            response: { body: CrewMerchantUi.signedUrlSchema },
        },
    },
    [customerPaymentIdRefundPath]: {
        path: customerPaymentIdRefundPath,
        post: {
            request: {
                params: customerRefundIdParams,
            },
            response: { body: CrewMerchantUi.claimCustomerPaymentSchema },
        },
    },
};
export const crewMerchantStoreRuleApi = {
    [storeRulePath]: {
        path: storeRulePath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.storeRuleSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.storeRuleCreateSchema,
            },
            response: { body: CrewMerchantUi.storeRuleSchema },
        },
    },
    [storeRuleIdPath]: {
        path: storeRuleIdPath,
        get: {
            request: { params: storeRuleIdParams },
            response: { body: CrewMerchantUi.storeRuleSchema },
        },
        put: {
            request: {
                params: storeRuleIdParams,
                body: CrewMerchantUi.storeRuleUpdateSchema,
            },
            response: { body: CrewMerchantUi.storeRuleSchema },
        },
        delete: {
            request: {
                params: storeRuleIdParams,
            },
            response: { body: CrewMerchantUi.apiClientRespSchema },
        },
    },
    [factValuesPath]: {
        path: factValuesPath,
        get: {
            request: { params: storeIdParams },
            response: { body: CrewMerchantUi.arrayFactDataSchema },
        },
    },
};
export const crewMerchantApi = {
    [storeIdPath]: {
        path: storeIdPath,
        use: storeIdParams,
    },
    [shippingProtectionRatePath]: {
        path: shippingProtectionRatePath,
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.protectionRateCreateSchema,
            },
            response: {
                body: CrewMerchantUi.protectionRateSchema,
            },
        },
        get: {
            request: {
                params: storeIdParams,
            },
            response: {
                body: CrewMerchantUi.protectionRateSchema.array(),
            },
        },
    },
    [shippingProtectionRateIdPath]: {
        path: shippingProtectionRateIdPath,
        put: {
            request: {
                params: shippingProtectionRateIdParams,
                body: CrewMerchantUi.protectionRateUpdateSchema,
            },
            response: {
                body: CrewMerchantUi.protectionRateSchema,
            },
        },
        delete: {
            request: {
                params: shippingProtectionRateIdParams,
            },
            response: {
                body: apiClientResp,
            },
        },
    },
    [shippingProtectionConfigurePath]: {
        path: shippingProtectionConfigurePath,
        post: {
            request: {
                body: z.object({
                    method: enumToZodLiteralUnion(ConfigureShippingMethod),
                }),
            },
            response: {
                body: CrewMerchantUi.shipProtectSettingsSchema,
            },
        },
    },
    [invoicePath]: {
        path: invoicePath,
        get: {
            request: { params: storeIdParams },
            response: { body: CrewMerchantUi.invoiceSchema.array() },
        },
    },
    [orderPath]: {
        path: orderPath,
        get: {
            request: { params: storeIdParams, query: storeOrderQuery },
            response: { body: crewOrder },
        },
    },
    [userPath]: {
        path: userPath,
        get: {
            request: { params: userParams },
            response: { body: CrewMerchantUi.userWithStoreUserRolesSchema },
        },
    },
    [storeCustomFieldPath]: {
        path: storeCustomFieldPath,
        get: {
            request: {},
            response: { body: z.array(CrewMerchantUi.customFieldSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.customFieldCreateSchema,
            },
            response: { body: CrewMerchantUi.customFieldSchema },
        },
    },
    [storeCustomFieldIdPath]: {
        path: storeCustomFieldIdPath,
        get: {
            request: { params: customFieldIdParams },
            response: { body: CrewMerchantUi.customFieldSchema },
        },
        put: {
            request: {
                params: customFieldIdParams,
                body: CrewMerchantUi.customFieldUpdateSchema,
            },
            response: { body: CrewMerchantUi.customFieldSchema },
        },
        delete: {
            request: {
                params: customFieldIdParams,
            },
            response: { body: CrewMerchantUi.apiClientRespSchema },
        },
    },
    [storeClaimTagPath]: {
        path: storeClaimTagPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.claimTagSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.claimTagCreateSchema,
            },
            response: { body: CrewMerchantUi.claimTagSchema },
        },
    },
    [storeClaimTagIdPath]: {
        path: storeClaimTagIdPath,
        delete: {
            request: { params: storeIdTagParams },
            response: { body: apiClientResp },
        },
    },
    [settingsThemePath]: {
        path: settingsThemePath,
        get: {
            request: { params: storeIdParams },
            response: { body: CrewMerchantUi.settingsThemeSchema },
        },
        put: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.settingsThemeSchema,
            },
            response: { body: CrewMerchantUi.settingsThemeSchema },
        },
    },
    [settingsIntegrationPath]: {
        path: settingsIntegrationPath,
        get: {
            request: { params: storeIdParams },
            response: { body: CrewMerchantUi.settingsIntegrationSchema },
        },
        put: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.settingsIntegrationUpdateSchema,
            },
            response: { body: CrewMerchantUi.settingsIntegrationSchema },
        },
    },
    [settingsConfigPath]: {
        path: settingsConfigPath,
        get: {
            request: { params: storeIdParams },
            response: { body: CrewMerchantUi.settingsConfigSchema },
        },
        put: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.settingsConfigSchema,
            },
            response: { body: CrewMerchantUi.settingsConfigSchema },
        },
    },
    [storeProductPath]: {
        path: storeProductPath,
        get: {
            request: { params: storeIdParams, query: storeProductQuery },
            response: { body: z.array(CrewMerchantUi.storeProductSchema) },
        },
    },
    [storeProductSyncPath]: {
        path: storeProductSyncPath,
        post: {
            request: { params: storeIdParams },
            response: { body: apiClientResp },
        },
    },
    [storeProductIdPath]: {
        path: storeProductIdPath,
        get: {
            request: { params: storeIdProductIdFromPlatformParams },
            response: { body: variantExchange },
        },
    },
    [storeProductTagPath]: {
        path: storeProductTagPath,
        get: {
            request: { params: storeIdParams },
            response: {
                body: z.array(CrewMerchantUi.storeProductTagSchema),
            },
        },
    },
    [storeProductCollectionPath]: {
        path: storeProductCollectionPath,
        get: {
            request: { params: storeIdParams },
            response: {
                body: z.array(CrewMerchantUi.storeProductCollectionSchema),
            },
        },
    },
    [storeProductTypePath]: {
        path: storeProductTypePath,
        get: {
            request: { params: storeIdParams },
            response: {
                body: z.array(CrewMerchantUi.storeProductTypeSchema),
            },
        },
    },
    [storeProductGroupPath]: {
        path: storeProductGroupPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.storeProductGroupSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.storeProductGroupCreateSchema,
            },
            response: { body: CrewMerchantUi.storeProductGroupSchema },
        },
    },
    [storeProductGroupIdPath]: {
        path: storeProductGroupIdPath,
        put: {
            request: {
                params: storeProductGroupIdParams,
                body: CrewMerchantUi.storeProductGroupUpdateSchema,
            },
            response: { body: CrewMerchantUi.storeProductGroupSchema },
        },
        delete: {
            request: {
                params: storeProductGroupIdParams,
            },
            response: { body: CrewMerchantUi.apiClientRespSchema },
        },
    },
    [storeProductGroupIdListProductsPath]: {
        path: storeProductGroupIdListProductsPath,
        get: {
            request: {
                params: storeProductGroupIdParams,
            },
            response: { body: z.array(CrewMerchantUi.storeProductsForGroupSchema) },
        },
    },
    [storeProductGroupIdRefreshProductsPath]: {
        path: storeProductGroupIdRefreshProductsPath,
        post: {
            request: {
                params: storeProductGroupIdParams,
            },
            response: { body: apiClientResp },
        },
    },
    [returnLocationPath]: {
        path: returnLocationPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.returnLocationSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.returnLocationCreateSchema,
            },
            response: { body: CrewMerchantUi.returnLocationSchema },
        },
    },
    [returnLocationIdPath]: {
        path: returnLocationIdPath,
        put: {
            request: {
                params: returnLocationIdParams,
                body: CrewMerchantUi.returnLocationSchema,
            },
            response: { body: CrewMerchantUi.returnLocationSchema },
        },
        delete: {
            request: {
                params: returnLocationIdParams,
            },
            response: { body: CrewMerchantUi.apiClientRespSchema },
        },
    },
    [storeUserPath]: {
        path: storeUserPath,
        get: {
            request: {
                params: storeIdParams,
            },
            response: {
                body: z.array(CrewMerchantUi.storeUserSchema),
            },
        },
        post: {
            request: {
                body: CrewMerchantUi.storeUserCreateSchema,
                params: storeIdParams,
            },
            response: {
                body: CrewMerchantUi.storeUserSchema,
            },
        },
    },
    [storeUserSignupPath]: {
        path: storeUserSignupPath,
        post: {
            request: {
                body: CrewMerchantUi.storeUserSignupCreateSchema,
                params: storeIdParams,
            },
            response: {
                body: CrewMerchantUi.storeUserSignupCreateSchema,
            },
        },
    },
    [storeUserIdPath]: {
        path: storeUserIdPath,
        put: {
            request: {
                params: storeUserIdParams,
                body: CrewMerchantUi.storeUserUpdateSchema,
            },
            response: {
                body: CrewMerchantUi.storeUserSchema,
            },
        },
        delete: {
            request: {
                params: storeUserIdParams,
            },
            response: {
                body: CrewMerchantUi.apiClientRespSchema,
            },
        },
    },
    [storeUserPasswordResetPath]: {
        path: storeUserPasswordResetPath,
        post: {
            request: {
                params: storeUserIdParams,
                body: CrewMerchantUi.storeUserSchema,
            },
            response: {
                body: CrewMerchantUi.storeUserSchema,
            },
        },
    },
};
// ? we may want some kind of satisfier on the schema to ensure it matches requirements of URLs; i.e. all path params and query params are strings unless coerced
// eslint-disable-next-line @typescript-eslint/no-namespace
export var CrewMerchantApi;
(function (CrewMerchantApi) {
    CrewMerchantApi.loggedInUserName = 'x-corso-user-name';
    CrewMerchantApi.loggedInUserIdFromPlatform = 'x-corso-user-id-from-platform';
})(CrewMerchantApi || (CrewMerchantApi = {}));
//# sourceMappingURL=api.js.map